// PersistentAI — Stage 1 shell.
// App-level chat panel. Resizable + collapsible. Conversation persisted
// to localStorage. Sends to the LEGACY /api/v1/ai/chat endpoint with
// the existing request shape. Stage 2 introduces the context provider;
// Stage 3 swaps in the new endpoint. Until then this is intentionally
// a thin shell — visual style mirrors the deleted AIPanel.js so users
// see continuity.

import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { themeColors } from '../theme';
import { WorkspaceContext } from '../context/WorkspaceContext';
import { buildKBContext } from '../services/kbContextBuilder';
import LiteratureSuggestForm from './LiteratureSuggestForm';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;
// KB router is mounted at /kb (NOT under /api/v1) — same convention as
// services/kbService.js.
const KB_API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/kb`;

// ── V1.0-beta — single merged chat ──────────────────────────────────────────
// The Setup Assistant / Diagnostic mode tabs were merged into one chat
// (2026-07 beta polish): the backend now infers setup vs diagnostic intent
// per message. We always send the unified mode; the backend keeps accepting
// explicit 'setup'/'diagnostic' for API clients.
const AI_MODE = 'general';

// KB health probe — done locally (not via kbService.getHealth) because the
// About modal also needs the `ai` config flags (consensus availability)
// that /kb/health now returns and kbService strips.
async function fetchKbHealth() {
  try {
    const { data } = await axios.get(`${KB_API}/health`, { timeout: 8000 });
    return {
      ok: true,
      total_rows: data.total_rows || 0,
      tables: data.tables || {},
      ai: data.ai || null,
    };
  } catch (err) {
    console.warn('[PersistentAI] KB health check failed:', err?.message || err);
    return { ok: false, total_rows: 0, tables: {}, ai: null };
  }
}

// ── localStorage keys ──────────────────────────────────────────────────────
const LS_HISTORY  = 'jvista_chat_history_v1';
const LS_ARCHIVE  = 'jvista_chat_history_archive_v1';
const LS_WIDTH    = 'jvista_chat_width_v1';
const LS_COLLAPSED = 'jvista_chat_collapsed_v1';

// ── Width bounds ───────────────────────────────────────────────────────────
const DEFAULT_WIDTH = 400;
const MIN_WIDTH = 320;
const maxWidthForViewport = () => Math.min(800, Math.floor(window.innerWidth * 0.5));

// ── Viewport-default rule (Q1 resolution): expanded on >=1280, else collapsed ─
const VIEWPORT_EXPAND_THRESHOLD = 1280;
function defaultCollapsedForViewport() {
  return window.innerWidth < VIEWPORT_EXPAND_THRESHOLD;
}

// ── Initial conversation seed ──────────────────────────────────────────────
const SEED_MESSAGE = {
  role: 'assistant',
  content: "Hello! I'm JVISTA AI. Ask me about your simulation results, device physics, or solar cell design.",
  ts: new Date().toISOString(),
};

function genId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// RFC4122 v4 session id used to group the thumbs-up/down ratings a user
// gives during one chat session. Prefers the native crypto.randomUUID;
// falls back to Math.random for older / insecure-context browsers.
function genSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ── Message sanitiser ─────────────────────────────────────────────────────
// Pure function — exported for unit testing. Filters a messages array
// down to entries that are safe to persist AND safe to send to the
// backend. Three reasons a message is dropped:
//   1. Malformed: not an object, unknown role, or missing/empty content.
//      These break the backend's Pydantic validation (Stage 3
//      ChatRequest requires non-empty content on every conversation
//      history entry). Logged via console.warn when `logWarnings` is on.
//   2. Error responses (isError: true). These are transient by policy —
//      shown in the UI for the current session, but never persisted
//      or replayed to the backend.
// Errors are dropped silently because they're expected; malformed
// entries are logged because they indicate corrupted state.
export function sanitizeMessages(messages, { logWarnings = false } = {}) {
  if (!Array.isArray(messages)) return [];
  const cleaned = [];
  for (const m of messages) {
    if (!m || typeof m !== 'object') {
      if (logWarnings) {
        console.warn('[PersistentAI] dropping non-object entry from chat history');
      }
      continue;
    }
    if (m.role !== 'user' && m.role !== 'assistant' && m.role !== 'system') {
      if (logWarnings) {
        console.warn('[PersistentAI] dropping chat message with bad role:', {
          ts: m.ts || (m.metadata && m.metadata.ts) || null,
          role: m.role,
        });
      }
      continue;
    }
    if (typeof m.content !== 'string' || m.content.trim().length === 0) {
      if (logWarnings) {
        console.warn(
          '[PersistentAI] dropping malformed chat message (missing/empty content):',
          {
            ts: m.ts || (m.metadata && m.metadata.ts) || null,
            role: m.role,
          }
        );
      }
      continue;
    }
    // Error responses are transient — never persisted, never replayed.
    if (m.isError === true) continue;
    cleaned.push(m);
  }
  return cleaned;
}

function loadHistory() {
  try {
    const raw = localStorage.getItem(LS_HISTORY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.messages)) return null;

    // One-time migration: defensively clean any malformed entries that
    // may have leaked into storage (e.g., from previous error-handling
    // bugs). If anything was dropped, rewrite the cleaned list back to
    // localStorage so future loads start clean.
    const original = parsed.messages;
    const cleaned = sanitizeMessages(original, { logWarnings: true });
    if (cleaned.length === original.length) {
      return parsed;
    }
    console.warn(
      `[PersistentAI] cleaned ${original.length - cleaned.length} message(s) ` +
      `from chat history during load; rewriting localStorage`
    );
    const fixedConv = { ...parsed, messages: cleaned };
    try {
      localStorage.setItem(LS_HISTORY, JSON.stringify(fixedConv));
    } catch { /* best effort */ }
    return fixedConv;
  } catch {
    return null;
  }
}

function saveHistory(conv) {
  try {
    // Defensive — strip error/malformed entries before writing. Errors
    // get into in-memory `conv.messages` because we render them in the
    // UI for the current session, but they must never reach disk.
    const safeConv = {
      ...conv,
      messages: sanitizeMessages(conv && conv.messages),
    };
    localStorage.setItem(LS_HISTORY, JSON.stringify(safeConv));
  } catch (e) {
    console.warn('[PersistentAI] failed to save history:', e);
  }
}

function loadArchive() {
  try {
    const raw = localStorage.getItem(LS_ARCHIVE);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveArchive(archive) {
  try {
    localStorage.setItem(LS_ARCHIVE, JSON.stringify(archive));
  } catch (e) {
    console.warn('[PersistentAI] failed to save archive:', e);
  }
}

function newConversation() {
  return {
    conversation_id: genId(),
    started_at: new Date().toISOString(),
    messages: [SEED_MESSAGE],
  };
}

// ── Active-context strip formatter ─────────────────────────────────────
// One-line human summary used in the strip above the conversation. Reads
// the active tab and its published state. Tabs that publish
// { status: 'context_pending' } render with a "pending" suffix.
const TAB_LABEL = {
  simulation:   'Simulation',
  profiles:     'Profiles',
  curva:        'CurVA',
  avarodha:     'Avarodha',
  pl_trpl:      'PL/TRPL',
  eqe:          'EQE',
  cv:           'C-V',
  pl_analysis:  'PL Analysis',
  parameter_library: 'Parameter Library',
  feedback:     'Feedback',
  device_modal: 'Device Editor',
};

function buildContextStrip(workspace) {
  if (!workspace) return 'context: …';
  const { active_tab, tab_state, latest_simulation } = workspace;
  const slot = tab_state?.[active_tab] || {};
  const label = TAB_LABEL[active_tab] || active_tab || '?';
  const parts = [label];
  if (slot.status === 'context_pending') {
    parts.push('pending');
    return 'context: ' + parts.join(' · ');
  }
  switch (active_tab) {
    case 'simulation': {
      if (slot.stack_summary) parts.push(slot.stack_summary);
      else if (slot.layer_count === 0) parts.push('no layers');
      if (latest_simulation?.metrics?.Voc != null) {
        parts.push(`last run Voc=${Number(latest_simulation.metrics.Voc).toFixed(2)} V`);
      } else if (slot.status === 'running') {
        parts.push('solving…');
      }
      break;
    }
    case 'curva': {
      if (slot.data_source) parts.push(slot.data_source);
      if (slot.has_uploaded_jv && slot.uploaded_meta?.n_points) {
        parts.push(`upload ${slot.uploaded_meta.n_points} pts`);
      }
      if (slot.cursors && slot.cursors.v0 != null && slot.cursors.v1 != null) {
        parts.push(`cursors [${Number(slot.cursors.v0).toFixed(2)}, ${Number(slot.cursors.v1).toFixed(2)}]`);
      }
      break;
    }
    case 'avarodha': {
      if (!slot.has_results) parts.push('no results');
      if (slot.voc_ceiling != null) {
        parts.push(`ceiling ${Number(slot.voc_ceiling).toFixed(2)} V`);
      }
      const oc = slot.overlay_results?.length || 0;
      if (oc) parts.push(`${oc} overlay${oc === 1 ? '' : 's'}`);
      break;
    }
    case 'eqe': {
      if (slot.wl_start_nm != null && slot.wl_end_nm != null) {
        parts.push(`λ ${slot.wl_start_nm}–${slot.wl_end_nm} nm`);
      }
      if (slot.sweeping) parts.push('sweeping');
      else if (slot.jsc_eqe_mA != null) parts.push(`Jsc=${Number(slot.jsc_eqe_mA).toFixed(1)} mA/cm²`);
      break;
    }
    case 'cv': {
      if (slot.active_subtab) parts.push(slot.active_subtab);
      if (slot.is_calculating) parts.push('calculating');
      if (slot.fit_summary?.Vbi != null) parts.push(`Vbi=${Number(slot.fit_summary.Vbi).toFixed(2)} V`);
      break;
    }
    case 'pl_trpl': {
      if (slot.mode) parts.push(`${slot.mode} mode`);
      const tc = slot.applied_traps?.length || 0;
      if (tc) parts.push(`${tc} trap${tc === 1 ? '' : 's'}`);
      if (slot.has_experimental_trpl) parts.push('expt loaded');
      break;
    }
    case 'pl_analysis': {
      if (slot.material) parts.push(slot.material);
      if (slot.spectra_count) parts.push(`${slot.spectra_count} spectra`);
      const dq = slot.defect_queue?.length || 0;
      if (dq) parts.push(`${dq} peak${dq === 1 ? '' : 's'} queued`);
      break;
    }
    case 'profiles': {
      parts.push(slot.has_data ? 'data present' : 'no data');
      break;
    }
    case 'parameter_library': {
      if (slot.material_label) {
        parts.push(`Browsing: ${slot.material_label}${slot.category_label ? ` (${slot.category_label})` : ''}`);
      } else if (slot.fetch_error) {
        parts.push('library unavailable');
      }
      break;
    }
    case 'feedback': {
      if (slot.draft_present) parts.push(`drafting ${slot.draft_kind || ''}`.trim());
      else parts.push('no draft');
      break;
    }
    case 'device_modal': {
      if (slot.section) parts.push(`${slot.section} section`);
      if (slot.selected_layer_name) parts.push(`${slot.selected_layer_name} selected`);
      break;
    }
    default: break;
  }
  return 'context: ' + parts.join(' · ');
}

export default function PersistentAI({ results, theme = 'dark', onDeviceSpecLoad }) {
  const c = themeColors(theme);
  const workspace = useContext(WorkspaceContext);

  // ── State ────────────────────────────────────────────────────────────
  const [conv, setConv] = useState(() => loadHistory() || newConversation());
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('Thinking...');

  // ── Feedback session id ──────────────────────────────────────────────
  // One UUID per component mount, sent with every thumbs-up/down rating so
  // the backend can group a session's feedback. Stable for this mount.
  const [sessionId] = useState(genSessionId);

  // ── Session C — KB grounding (single merged chat mode, V1.0-beta) ────
  const [kbContext, setKbContext] = useState('');      // built from workspace device/tech
  const [kbLoading, setKbLoading] = useState(false);  // true while Tier 1 is in-flight
  const [kbHealth, setKbHealth] = useState({ ok: null, total_rows: 0, ai: null }); // ok:null = checking
  const [aboutOpen, setAboutOpen] = useState(false);  // "About the AI" modal

  // Width / collapsed — seeded with localStorage > viewport default fallback.
  const [width, setWidth] = useState(() => {
    const stored = parseInt(localStorage.getItem(LS_WIDTH) || '', 10);
    if (Number.isFinite(stored) && stored >= MIN_WIDTH) {
      return Math.min(stored, maxWidthForViewport());
    }
    return DEFAULT_WIDTH;
  });
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(LS_COLLAPSED);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return defaultCollapsedForViewport();
  });

  // Dragging state for resize handle
  const [dragging, setDragging] = useState(false);

  // Confirm dialog for New Conversation
  const [showNewConvConfirm, setShowNewConvConfirm] = useState(false);

  const endRef = useRef(null);
  const widthSaveTimer = useRef(null);

  // ── Persist conversation on every message change ──────────────────────
  useEffect(() => {
    saveHistory(conv);
  }, [conv]);

  // ── Persist width (debounced) ────────────────────────────────────────
  useEffect(() => {
    if (widthSaveTimer.current) clearTimeout(widthSaveTimer.current);
    widthSaveTimer.current = setTimeout(() => {
      localStorage.setItem(LS_WIDTH, String(width));
      // Notify the WorkspaceContext provider so its `ui.chat_width_px`
      // tracks. We can't rely on the 'storage' event — it only fires
      // cross-window. Same-window listeners use this CustomEvent.
      window.dispatchEvent(new CustomEvent('jvista-chat-ui-change',
        { detail: { width, collapsed } }));
    }, 200);
    return () => clearTimeout(widthSaveTimer.current);
  }, [width, collapsed]);

  // ── Persist collapsed state — only after user has interacted.
  // We track interaction via a ref to avoid persisting the
  // viewport-defaulted value on first mount.
  const userToggledCollapse = useRef(false);
  useEffect(() => {
    if (userToggledCollapse.current) {
      localStorage.setItem(LS_COLLAPSED, String(collapsed));
      window.dispatchEvent(new CustomEvent('jvista-chat-ui-change',
        { detail: { width, collapsed } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  // ── Update CSS custom property for the workspace padding-right ────────
  useEffect(() => {
    const effective = collapsed ? 0 : width;
    document.documentElement.style.setProperty('--chat-width', `${effective}px`);
  }, [width, collapsed]);

  // ── Window resize — clamp width if viewport shrank ───────────────────
  useEffect(() => {
    function onResize() {
      const max = maxWidthForViewport();
      setWidth(w => Math.min(w, max));
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Loading label cycling (carried over from AIPanel.js:20-26) ───────
  useEffect(() => {
    if (!loading) return;
    setLoadingLabel('Thinking...');
    const t1 = setTimeout(() => setLoadingLabel('🔍 Searching literature...'), 2500);
    const t2 = setTimeout(() => setLoadingLabel('Synthesizing response...'), 8000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [loading]);

  // ── Auto-scroll on new message ───────────────────────────────────────
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conv.messages.length, loading]);

  // ── Session C — KB health badge (check once on mount) ────────────────
  useEffect(() => {
    let cancelled = false;
    fetchKbHealth().then(h => { if (!cancelled) setKbHealth(h); });
    return () => { cancelled = true; };
  }, []);

  // ── Session C — rebuild KB context (two-tier) ────────────────────────
  // Device/workspace changes are debounced 400ms to avoid spamming the API
  // during rapid edits. kbLoading is true until Tier 1 resolves.
  const deviceSpec = workspace?.device_spec;
  const latestSim = workspace?.latest_simulation;
  useEffect(() => {
    let cancelled = false;
    setKbLoading(true);

    const doFetch = () => {
      buildKBContext(
        { device_spec: deviceSpec, latest_simulation: latestSim },
        { aiMode: AI_MODE },
        (ctx, tier) => {
          if (cancelled) return;
          setKbContext(ctx || '');
          if (tier === 'tier1') setKbLoading(false);
        }
      ).then(finalCtx => {
        // Handles early-return '' case (no device) where onUpdate is never called.
        if (!cancelled) { setKbContext(finalCtx || ''); setKbLoading(false); }
      }).catch(() => {
        if (!cancelled) { setKbContext(''); setKbLoading(false); }
      });
    };

    const t = setTimeout(doFetch, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [deviceSpec, latestSim]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Escape key — collapse panel when expanded ────────────────────────
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && !collapsed) {
        userToggledCollapse.current = true;
        setCollapsed(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [collapsed]);

  // ── Resize handle drag ───────────────────────────────────────────────
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    function onMove(e) {
      const newWidth = window.innerWidth - e.clientX;
      const max = maxWidthForViewport();
      const clamped = Math.max(MIN_WIDTH, Math.min(max, newWidth));
      setWidth(clamped);
    }
    function onUp() {
      setDragging(false);
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [dragging]);

  // ── Toggle collapse ──────────────────────────────────────────────────
  function toggleCollapse() {
    userToggledCollapse.current = true;
    setCollapsed(v => !v);
  }

  // ── Archive current, start new ───────────────────────────────────────
  function handleNewConversation() {
    setShowNewConvConfirm(true);
  }

  // Clear only — start fresh WITHOUT archiving (user request 2026-07-03:
  // archive existed, plain clear did not).
  function confirmClearConversation() {
    setConv(newConversation());
    setShowNewConvConfirm(false);
  }

  function confirmNewConversation() {
    // Archive only if the current conversation has user-authored content
    // (so we don't archive the empty seed on first load).
    const hasUserMessages = conv.messages.some(m => m.role === 'user');
    if (hasUserMessages) {
      const archive = loadArchive();
      archive.unshift({
        ...conv,
        ended_at: new Date().toISOString(),
        title: `Conversation ${new Date(conv.started_at).toLocaleString()}`,
      });
      saveArchive(archive);
    }
    setConv(newConversation());
    setShowNewConvConfirm(false);
  }

  // ── Send a message ───────────────────────────────────────────────────
  async function handleSend() {
    const msg = input.trim();
    if (!msg || loading) return;

    // Capture the active context AT SUBMIT TIME so the chat sees the
    // workspace the user was looking at, not whatever it becomes by the
    // time the response lands.
    const activeContextSnapshot = workspace ? {
      tab:               workspace.active_tab || 'simulation',
      tab_state:         workspace.tab_state || {},
      device_spec:       workspace.device_spec || null,
      sim_settings:      workspace.sim_settings || null,
      latest_simulation: workspace.latest_simulation || null,
      simulation_history: workspace.simulation_history || [],
      ui:                workspace.ui || null,
    } : { tab: 'simulation', tab_state: {} };

    const userMsg = {
      role: 'user',
      content: msg,
      ts: new Date().toISOString(),
      metadata: {
        ts: new Date().toISOString(),
        active_tab: activeContextSnapshot.tab,
      },
    };
    const nextMessages = [...conv.messages, userMsg];
    setConv(c => ({ ...c, messages: nextMessages }));
    setInput('');
    setLoading(true);

    // ── Stage 3 unified request shape ────────────────────────────────
    // Per design §5.2. Belt-and-suspenders sanitiser pass: drops any
    // malformed or error messages that may have crept into in-memory
    // state. The backend's Pydantic ChatRequest requires non-empty
    // content on every conversation_history entry, so a single bad
    // entry poisons the whole request with a 422. We also exclude
    // 'system' role messages here because the backend re-derives that
    // info from active_context.simulation_history.
    const conversationHistory = sanitizeMessages(nextMessages)
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .slice(-20)
      .map(m => ({
        role: m.role,
        content: m.content,
        metadata: m.metadata || { ts: m.ts || null },
      }));

    const sessionMetadata = {
      conversation_id: conv.conversation_id,
      started_at: conv.started_at,
      message_count: nextMessages.length,
    };

    // ── Bug 1 fix: build kb_context FRESH at send time. The useEffect-cached
    // `kbContext` state could be empty if the user sends before the effect
    // resolves, and (until kbContextBuilder.js was patched) returned "" in
    // general mode whenever no device was loaded. Passing `msg` as
    // messageHint lets buildKBContext detect e.g. "CdSeTe" in the question
    // and ground accordingly even without a device. Falls back to the
    // cached state on error.
    let resolvedKbContext = '';
    try {
      resolvedKbContext = (await buildKBContext(
        { device_spec: workspace?.device_spec,
          latest_simulation: workspace?.latest_simulation },
        { aiMode: AI_MODE, messageHint: msg },
      )) || '';
    } catch (err) {
      console.warn('[PersistentAI] buildKBContext at send failed:', err);
      resolvedKbContext = kbContext || '';
    }
    // DEBUG (kept during beta) — confirms exactly what's being POSTed.
    console.log('[PersistentAI] POST kb_context:',
                resolvedKbContext.length, 'chars',
                resolvedKbContext.slice(0, 200));

    try {
      const { data } = await axios.post(`${API}/ai/chat`, {
        message: msg,
        conversation_history: conversationHistory,
        active_context: activeContextSnapshot,
        session_metadata: sessionMetadata,
        // Session C — KB grounding: the backend folds these into the system
        // prompt (KB context block + mode-specific anti-hallucination rules).
        kb_context: resolvedKbContext,
        ai_mode: AI_MODE,
      });
      const assistantMsg = {
        role: 'assistant',
        content: data.message,
        ts: new Date().toISOString(),
        confidence_rating: data.confidence_rating || 'low',
        confidence_explanation: data.confidence_explanation || '',
        // Legacy literature-search rendering — backend still surfaces
        // these on metadata for transition compatibility.
        usedLiterature: !!data.metadata?.used_literature_search,
        searchedQueries: data.metadata?.searched_queries || [],
        sources: data.metadata?.sources || [],
        toolsUsed: data.metadata?.tools_used || [],
        actions: data.actions || [],
        // AI-proposed device spec (per CHAT_V2_SYSTEM_PROMPT > ## Device
        // Specification Output). When present, renders a "Load into
        // Simulator" card below the bubble; null otherwise.
        device_spec: data.device_spec || null,
        metadata: {
          ts: new Date().toISOString(),
          active_tab: activeContextSnapshot.tab,
          confidence_rating: data.confidence_rating || 'low',
        },
      };
      setConv(c => ({ ...c, messages: [...c.messages, assistantMsg] }));
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to reach AI service';
      const errMsg = {
        role: 'assistant',
        content: `Error: ${detail}`,
        ts: new Date().toISOString(),
        isError: true,
      };
      setConv(c => ({ ...c, messages: [...c.messages, errMsg] }));
    } finally {
      setLoading(false);
    }
  }

  // ── Render: floating icon when collapsed ─────────────────────────────
  if (collapsed) {
    return (
      <button
        className="chat-floating-icon"
        onClick={toggleCollapse}
        title="Open JVISTA AI"
        aria-label="Open JVISTA AI chat"
      >
        <span style={{ fontSize: 28, lineHeight: 1 }}>{'\u{1F4AC}'}</span>
      </button>
    );
  }

  const messages = conv.messages || [];
  const showTranscriptDownload = messages.filter(m => m.role !== 'system').length > 1;

  return (
    <>
      <aside
        className={`persistent-ai ${dragging ? 'dragging' : ''}`}
        style={{ width: `${width}px` }}
      >
        {/* Resize handle on the left edge */}
        <div
          className={`chat-resize-handle ${dragging ? 'dragging' : ''}`}
          onMouseDown={handleResizeStart}
          title="Drag to resize"
          aria-label="Resize chat panel"
        />

        {/* Header — title on its own line, action buttons below it */}
        <div className="chat-header">
          <div className="chat-header-top">
            <div className="chat-title">
              JVISTA AI
              {results?.metrics && (
                <span className="chat-metrics">
                  Voc={results.metrics.Voc}V PCE={results.metrics.PCE}%
                </span>
              )}
            </div>
            <button
              className="chat-icon-btn"
              onClick={toggleCollapse}
              title="Collapse panel"
              aria-label="Collapse chat panel"
            >
              {'<'}
            </button>
          </div>
          <div className="chat-header-actions">
            <button
              className="chat-icon-btn"
              onClick={handleNewConversation}
              title="Start a new conversation (current will be archived)"
              aria-label="New conversation"
            >
              {'⊕'} New
            </button>
            <button
              className="chat-icon-btn"
              disabled
              title="Coming soon: switch between past conversations"
              aria-label="Conversation archive (coming soon)"
              style={{ opacity: 0.4, cursor: 'not-allowed' }}
            >
              {'⌃'} Archive
            </button>
            {showTranscriptDownload && (
              <DownloadTranscriptButton messages={messages} results={results} />
            )}
          </div>
        </div>

        {/* V1.0-beta — "About the AI" affordance (replaces the raw
            "KB: N entries" chip, which meant nothing to most users).
            Keeps the reachability dot; the entry count moved into the
            modal where it can be explained. */}
        <div
          className="chat-kb-badge"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 12px', fontSize: 11, color: c.textMuted,
          }}
        >
          <span
            title={
              kbHealth.ok
                ? 'Knowledge base reachable — answers are grounded in KB values'
                : kbHealth.ok === null
                  ? 'Checking knowledge base…'
                  : 'Knowledge base offline — AI cannot cite KB values'
            }
            style={{
              width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
              background: kbHealth.ok ? '#39d353' : '#888',
            }}
          />
          <button
            onClick={() => setAboutOpen(true)}
            title="What powers these answers — and how to help improve them"
            aria-label="About the AI"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: c.textMuted, fontSize: 11, padding: 0,
              fontFamily: 'inherit', textDecoration: 'underline',
              textUnderlineOffset: 2,
            }}
          >
            {'ℹ️'} About the AI
          </button>
        </div>

        {/* Active-context strip — Stage 2 — shows what the chat sees. */}
        {workspace && (
          <div
            className="chat-context-strip"
            title="What the chat sees as your active workspace context"
          >
            {buildContextStrip(workspace)}
          </div>
        )}

        {/* Messages */}
        <div className="chat-messages">
          {messages.map((msg, i) => {
            if (msg.role === 'system') {
              return (
                <div key={i} className="chat-system-note">
                  <span className="chat-system-note-text">{msg.content}</span>
                </div>
              );
            }
            const isUser = msg.role === 'user';
            // Feedback only on real AI answers: skip user turns, the seed
            // greeting (always index 0), and transient error bubbles.
            const showFeedback = !isUser && !msg.isError && i > 0;
            return (
              <React.Fragment key={i}>
                <div
                  className={`chat-bubble-row ${isUser ? 'user' : 'assistant'}`}
                >
                  <div
                    className={`chat-bubble ${isUser ? 'user' : 'assistant'} ${msg.isError ? 'error' : ''}`}
                    style={{
                      background: isUser ? c.aiUserBg : c.bgSecondary,
                      border: isUser ? `1px solid ${c.aiUserBg}` : `1px solid ${c.border}`,
                      color: isUser ? '#ffffff' : c.text,
                    }}
                  >
                    {msg.usedLiterature && (
                      <div className="chat-lit-banner" style={{ color: c.accentDark }}>
                        {'📚'} Sourced from literature
                        {msg.searchedQueries?.length > 0 && (
                          <span className="chat-lit-queries" style={{ color: c.textMuted }}>
                            {' — '}{msg.searchedQueries.join(' · ')}
                          </span>
                        )}
                      </div>
                    )}
                    {msg.content}
                    {/* Stage 3 — confidence rating chip. Stage 6 swaps in
                        color-coded borders + icon set + explainer modal. */}
                    {!isUser && msg.confidence_rating && (
                      <ConfidenceChip
                        rating={msg.confidence_rating}
                        explanation={msg.confidence_explanation}
                      />
                    )}
                  </div>
                </div>
                {!isUser && msg.device_spec && (
                  <DeviceSpecCard
                    spec={msg.device_spec}
                    onLoad={onDeviceSpecLoad}
                    c={c}
                  />
                )}
                {showFeedback && (
                  <MessageFeedback messageIndex={i} sessionId={sessionId} c={c} />
                )}
              </React.Fragment>
            );
          })}
          {loading && (
            <div className="chat-bubble-row assistant">
              <div
                className="chat-bubble assistant loading"
                style={{
                  background: c.bgSecondary,
                  border: `1px solid ${c.border}`,
                  color: c.textMuted,
                }}
              >
                {loadingLabel}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* V1.0-beta: the Setup Assistant / Diagnostic mode tabs were merged
            into this single chat — the AI infers intent per message. */}

        {/* KB loading indicator — shown above the input while Tier 1 is in-flight */}
        {kbLoading && (
          <div style={{
            padding: '2px 14px 0',
            fontSize: 10,
            color: c.textMuted,
            letterSpacing: 0.3,
            fontFamily: 'var(--mono, monospace)',
          }}>
            Loading KB context…
          </div>
        )}

        {/* Input */}
        <div className="chat-input-row">
          <textarea
            className="chat-textarea"
            placeholder="Ask about device physics, results, or parameters..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={loading}
            rows={2}
            style={{
              background: c.input,
              border: `1px solid ${c.border}`,
              color: c.inputText,
            }}
          />
          <button
            className="chat-send-btn"
            onClick={handleSend}
            disabled={loading || !input.trim()}
            style={{
              background: loading || !input.trim() ? c.btnInactive : c.btnPrimary,
              color: loading || !input.trim() ? c.btnInactiveText : c.btnPrimaryText,
            }}
          >
            Send
          </button>
        </div>

        {/* New Conversation confirm dialog */}
        {showNewConvConfirm && (
          <div className="chat-confirm-overlay">
            <div className="chat-confirm-dialog" style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
              <div className="chat-confirm-title">Start a new conversation?</div>
              <div className="chat-confirm-body" style={{ color: c.textMuted }}>
                Archive keeps the current conversation for later browsing
                (picker coming). Clear discards it permanently and starts
                fresh.
              </div>
              <div className="chat-confirm-actions">
                <button
                  onClick={() => setShowNewConvConfirm(false)}
                  style={{ background: c.btnSecondary, color: c.btnSecondaryText, border: `1px solid ${c.border}` }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmClearConversation}
                  title="Discard this conversation permanently and start fresh"
                  style={{ background: c.btnSecondary, color: c.btnSecondaryText, border: `1px solid ${c.border}` }}
                >
                  Clear without archiving
                </button>
                <button
                  onClick={confirmNewConversation}
                  style={{ background: c.btnPrimary, color: c.btnPrimaryText, border: 'none' }}
                >
                  Archive &amp; start new
                </button>
              </div>
            </div>
          </div>
        )}

        {/* V1.0-beta — About the AI modal (KB explainer + literature
            suggestion form). */}
        {aboutOpen && (
          <AboutAIModal
            kbHealth={kbHealth}
            c={c}
            onClose={() => setAboutOpen(false)}
          />
        )}

      </aside>

      {/* Backdrop for 768-1024 overlay mode — pure CSS shows/hides via media query. */}
      <div className="chat-overlay-backdrop" aria-hidden="true" />
    </>
  );
}

// ── About the AI modal (V1.0-beta) ─────────────────────────────────────────
// Plain-language explanation of what grounds the chat's answers, honest
// limits, and a "suggest literature" form that feeds the kb_suggestions
// review queue (POST /kb/suggest — nothing auto-ingests into the KB).
// Reuses the chat-confirm-overlay / chat-confirm-dialog styling so it
// matches the existing New-Conversation dialog.
function AboutAIModal({ kbHealth, c, onClose }) {
  // Literature-provider status — reported by /kb/health (provider
  // abstraction: Consensus MCP and/or Semantic Scholar). null = unknown
  // (health unreachable / older backend).
  const litProviders = kbHealth?.ai
    ? (kbHealth.ai.literature_providers
        || (kbHealth.ai.consensus_configured ? ['consensus'] : []))
    : null;
  const hasConsensus = Array.isArray(litProviders) && litProviders.includes('consensus');
  const hasS2 = Array.isArray(litProviders) && litProviders.includes('semantic_scholar');

  const sectionTitle = {
    fontWeight: 600, fontSize: 12, color: c.text, margin: '12px 0 4px',
  };
  const bodyText = {
    fontSize: 12, color: c.textMuted, lineHeight: 1.5, margin: 0,
  };

  return (
    <div className="chat-confirm-overlay" onClick={onClose}>
      <div
        className="chat-confirm-dialog"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label="About the AI"
        style={{
          background: c.bg, color: c.text, border: `1px solid ${c.border}`,
          maxWidth: 420, maxHeight: '80vh', overflowY: 'auto',
        }}
      >
        <div className="chat-confirm-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>About JVISTA AI</span>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: c.textMuted, fontSize: 14, padding: '0 2px',
            }}
          >
            {'✕'}
          </button>
        </div>

        <div style={sectionTitle}>What grounds the answers</div>
        <p style={bodyText}>
          The assistant answers from a curated knowledge base of solar-cell
          literature — validated material parameters, defect data, device
          phenomena, and efficiency records extracted from peer-reviewed
          papers
          {kbHealth?.ok
            ? ` (currently ${Number(kbHealth.total_rows || 0).toLocaleString()} entries)`
            : kbHealth?.ok === false ? ' (currently unreachable)' : ''}.
          It also sees your active workspace — device stack, latest
          simulation, and the tab you are on.
        </p>

        <div style={sectionTitle}>Live literature search</div>
        <p style={bodyText}>
          {litProviders === null && (
            'When configured, answers can also draw on a live search of peer-reviewed literature, marked "Sourced from literature".'
          )}
          {Array.isArray(litProviders) && litProviders.length === 0 && (
            'Live literature search is not enabled on this server right now — answers come from the knowledge base and your workspace context.'
          )}
          {hasConsensus && hasS2 && (
            'For physics and materials questions, answers can also draw on live searches of peer-reviewed literature — Consensus (primary) plus Semantic Scholar (supplementary). When that happens the answer is marked "Sourced from literature".'
          )}
          {hasConsensus && !hasS2 && (
            'For physics and materials questions, answers can also draw on a live search of peer-reviewed literature via Consensus. When that happens the answer is marked "Sourced from literature".'
          )}
          {!hasConsensus && hasS2 && (
            'For physics and materials questions, answers can also draw on a live search of peer-reviewed papers via Semantic Scholar. When that happens the answer is marked "Sourced from literature".'
          )}
        </p>

        <div style={sectionTitle}>Honest limits</div>
        <p style={bodyText}>
          The AI can be wrong. Every answer carries a confidence rating —
          treat "low" and "speculative" answers as starting points, not
          conclusions, and check the cited sources before relying on a
          number. If something looks off, use the thumbs-down to tell us.
        </p>

        <div style={sectionTitle}>Suggest literature for the knowledge base</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p style={bodyText}>
            Know a paper we should include? Suggestions go to a review
            queue — nothing is added automatically. Attach a PDF or give us a
            DOI/URL.
          </p>
          <LiteratureSuggestForm colors={c} compact />
        </div>

        <div className="chat-confirm-actions" style={{ marginTop: 14 }}>
          <button
            onClick={onClose}
            style={{
              background: c.btnSecondary, color: c.btnSecondaryText,
              border: `1px solid ${c.border}`,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Confidence chip (Stage 3) ─────────────────────────────────────────────
// Renders the AI's confidence rating below the message text. Stage 3
// is text + colored border-left only; Stage 6 adds the explainer-modal,
// icon set, and final palette per orchestrator design iteration.
const RATING_STYLE = {
  verified:     { label: 'Verified',     color: '#39d353' },
  high:         { label: 'High',         color: '#7ee787' },
  medium:       { label: 'Medium',       color: '#f0c000' },
  low:          { label: 'Low',          color: '#fb8500' },
  speculative:  { label: 'Speculative',  color: '#e85d75' },
  cannot_answer:{ label: "Can't answer", color: '#8b949e' },
};

// ── AI-proposed device spec card ────────────────────────────────────────────
// Renders below an assistant bubble when the chat response carried a
// device_spec block (per backend CHAT_V2_SYSTEM_PROMPT > ## Device
// Specification Output). Shows the device name, a brief stack summary,
// and a single "Load into Simulator" button. Clicking loads the device,
// flips the button to a "Thanks" / "Loaded" confirmation, and leaves
// the card visible so the user can scroll back to see what they loaded.
function DeviceSpecCard({ spec, onLoad, c }) {
  const [loaded, setLoaded] = useState(false);
  const layers = Array.isArray(spec?.stack) ? spec.stack : [];
  const handleClick = () => {
    if (loaded || !onLoad) return;
    onLoad(spec);
    setLoaded(true);
  };
  return (
    <div style={{
      marginTop: -6, marginBottom: 12,
      padding: '10px 12px',
      background: c.bgSecondary,
      border: `1px solid ${c.accent}`,
      borderLeft: `3px solid ${c.accent}`,
      borderRadius: 6,
      fontSize: 12,
      color: c.text,
    }}>
      <div style={{ fontWeight: 600, color: c.accent, marginBottom: 4 }}>
        🔧 {spec?.device_name || 'Proposed device'}
      </div>
      {layers.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11, color: c.textMuted }}>
          Stack ({layers.length} layer{layers.length === 1 ? '' : 's'}):
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, color: c.text }}>
            {layers.map((layer, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                <strong>{layer.material || '?'}</strong>
                {layer.thickness_nm != null && (
                  <> · {layer.thickness_nm} nm</>
                )}
                <span style={{ color: c.textMuted }}> · {layer.role}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {spec?.notes && (
        <div style={{
          marginTop: 8, fontSize: 11, color: c.textMuted, fontStyle: 'italic',
          lineHeight: 1.4,
        }}>
          {spec.notes}
        </div>
      )}
      <button
        onClick={handleClick}
        disabled={loaded || !onLoad}
        style={{
          marginTop: 10, width: '100%',
          padding: '8px 12px',
          background: loaded ? c.btnInactive : c.btnPrimary,
          color:      loaded ? c.btnInactiveText : c.btnPrimaryText,
          border: 'none', borderRadius: 4,
          fontFamily: 'var(--mono, monospace)', fontSize: 12, fontWeight: 600,
          cursor: (loaded || !onLoad) ? 'default' : 'pointer',
        }}
        title={onLoad ? 'Load this device into the simulator and open the editor' : 'Device-load handler not wired'}
      >
        {loaded ? '✓ Device loaded — ready to simulate' : 'Load into Simulator'}
      </button>
    </div>
  );
}

function ConfidenceChip({ rating, explanation }) {
  const style = RATING_STYLE[rating] || RATING_STYLE.low;
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div style={{
      marginTop: 8,
      paddingTop: 6,
      borderTop: '1px dashed rgba(255,255,255,0.08)',
      fontSize: 10,
      letterSpacing: 0.5,
      display: 'flex', alignItems: 'flex-start',
      gap: 6, flexWrap: 'wrap',
    }}>
      <span
        title={explanation || 'How well-grounded this answer is.'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '2px 7px',
          borderRadius: 3,
          background: 'transparent',
          border: `1px solid ${style.color}`,
          color: style.color,
          textTransform: 'uppercase',
          fontWeight: 500,
        }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          background: style.color, display: 'inline-block',
        }} />
        {style.label}
      </span>
      {explanation && (
        <button
          onClick={() => setExpanded(v => !v)}
          title="Why this rating?"
          style={{
            background: 'transparent', border: 'none',
            color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
            padding: '0 4px', fontFamily: 'inherit', fontSize: 10,
            lineHeight: 1,
          }}>
          {expanded ? '−' : '?'}
        </button>
      )}
      {expanded && explanation && (
        <div style={{
          width: '100%',
          marginTop: 4,
          padding: '5px 8px',
          borderRadius: 3,
          background: 'rgba(255,255,255,0.04)',
          color: 'rgba(255,255,255,0.7)',
          lineHeight: 1.45,
          letterSpacing: 0,
          textTransform: 'none',
          fontSize: 10,
        }}>
          {explanation}
        </div>
      )}
    </div>
  );
}

// ── Message feedback (thumbs up / down) ───────────────────────────────────
// Small, muted controls below each assistant answer. 👍 posts rating=+1
// immediately; 👎 reveals an optional "What was wrong?" field whose Submit
// posts rating=-1 with the correction text. After a successful POST the
// controls collapse to a "✓ Thanks" confirmation. Posts to
//   POST /api/v1/ai/feedback {session_id, message_index, rating, correction?}
// Fails soft: a network error returns the user to the controls with an
// inline retry hint, so a rating is never silently dropped.
function MessageFeedback({ messageIndex, sessionId, c }) {
  const [phase, setPhase] = React.useState('idle'); // idle | correcting | submitting | done
  const [correction, setCorrection] = React.useState('');
  const [error, setError] = React.useState('');

  async function postFeedback(rating, correctionText) {
    setPhase('submitting');
    setError('');
    try {
      const body = { session_id: sessionId, message_index: messageIndex, rating };
      const trimmed = (correctionText || '').trim();
      if (trimmed) body.correction = trimmed;
      await axios.post(`${API}/ai/feedback`, body);
      setPhase('done');
    } catch (err) {
      setError('Could not send — try again');
      // Return to the relevant entry point so the user can retry.
      setPhase(rating === -1 ? 'correcting' : 'idle');
    }
  }

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 8,
    margin: '-6px 0 12px',
    fontSize: 11, color: c.textMuted,
  };

  if (phase === 'done') {
    return <div style={rowStyle}>{'✓'} Thanks</div>;
  }

  if (phase === 'correcting') {
    return (
      <div style={{ ...rowStyle, flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
        <textarea
          autoFocus
          placeholder="What was wrong? (optional)"
          value={correction}
          onChange={e => setCorrection(e.target.value)}
          rows={2}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '6px 9px', borderRadius: 4,
            border: `1px solid ${c.border}`,
            background: c.input, color: c.inputText,
            fontFamily: 'var(--mono, monospace)', fontSize: 12,
            resize: 'none', outline: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => postFeedback(-1, correction)}
            style={{
              background: c.btnPrimary, color: c.btnPrimaryText,
              border: 'none', borderRadius: 4, padding: '4px 12px',
              fontFamily: 'var(--mono, monospace)', fontSize: 11,
              fontWeight: 500, cursor: 'pointer',
            }}
          >
            Submit
          </button>
          <button
            onClick={() => { setPhase('idle'); setError(''); }}
            style={{
              background: 'transparent', color: c.textMuted, border: 'none',
              fontFamily: 'var(--mono, monospace)', fontSize: 11, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          {error && <span style={{ color: c.red, fontSize: 11 }}>{error}</span>}
        </div>
      </div>
    );
  }

  // idle / submitting
  const disabled = phase === 'submitting';
  const iconBtn = {
    background: 'transparent', border: 'none', cursor: disabled ? 'default' : 'pointer',
    padding: '2px 4px', fontSize: 13, lineHeight: 1, opacity: 0.7,
  };
  return (
    <div style={rowStyle}>
      <button
        onClick={() => postFeedback(1)}
        disabled={disabled}
        title="Helpful"
        aria-label="Mark answer helpful"
        style={iconBtn}
      >
        {'\u{1F44D}'}
      </button>
      <button
        onClick={() => { setPhase('correcting'); setError(''); }}
        disabled={disabled}
        title="Not helpful"
        aria-label="Mark answer not helpful"
        style={iconBtn}
      >
        {'\u{1F44E}'}
      </button>
      {error && <span style={{ color: c.red, fontSize: 11 }}>{error}</span>}
    </div>
  );
}

// ── Transcript download (carried over from AIPanel.js:92-134) ─────────────
function DownloadTranscriptButton({ messages, results }) {
  return (
    <button
      className="chat-transcript-btn"
      onClick={() => {
        const ctx = results?.metrics || {};
        const lines = [];
        lines.push('JVISTA AI Chat Transcript');
        lines.push('='.repeat(50));
        lines.push(`Date: ${new Date().toLocaleString()}`);
        if (ctx.Voc != null || ctx.Jsc != null) {
          const fmt = (v, d = 3) => v != null ? Number(v).toFixed(d) : '—';
          lines.push(
            `Simulation: Voc=${fmt(ctx.Voc, 4)} V, ` +
            `Jsc=${fmt(ctx.Jsc != null ? Math.abs(ctx.Jsc) : null, 2)} mA/cm², ` +
            `FF=${fmt(ctx.FF, 1)}%, ` +
            `PCE=${fmt(ctx.PCE, 2)}%`
          );
        }
        lines.push('='.repeat(50));
        lines.push('');
        messages.forEach(msg => {
          if (msg.role === 'system') {
            lines.push(msg.content);
            lines.push('');
            return;
          }
          const role = msg.role === 'user' ? 'YOU' : 'JVISTA AI';
          lines.push(`[${role}]`);
          lines.push(msg.content);
          lines.push('');
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
        const link = document.createElement('a');
        link.download = `JVISTA_AI_Chat_${Date.now()}.txt`;
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
      }}
      title="Download chat transcript"
    >
      {'💾'} Download transcript
    </button>
  );
}
